/**
 * IntentBadge — labels the classified intent of a user message. `direct_answer` is the quiet default: no badge.
 * Clicking opens the classification detail when the parent passes `onClick`. Demo page: /ui-elements/badges.
 */
import type { ConversationIntentKind as GeneratedIntentKind } from '../../generated/api/types.gen.js';
import { Badge, type BadgeVariant } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';

export type ConversationIntentKind = GeneratedIntentKind;

interface IntentBadgeProps {
  kind: ConversationIntentKind;
  /** Optional click handler — opens a detail drawer in the parent. */
  onClick?: () => void;
  /** Tooltip text — typically the rationale + confidence. */
  title?: string;
}

const KIND_LABELS: Record<ConversationIntentKind, string> = {
  direct_answer: 'Direct answer',
  workflow: 'Workflow',
  task: 'Task',
  project: 'Project',
  marketplace: 'Marketplace',
  template: 'Template',
  workforce: 'Workforce',
  ambiguous: 'Needs clarification',
};

const KIND_VARIANT: Record<ConversationIntentKind, BadgeVariant> = {
  direct_answer: 'neutral',
  workflow: 'accent',
  task: 'warning',
  project: 'success',
  marketplace: 'accent',
  template: 'accent',
  workforce: 'success',
  ambiguous: 'neutral',
};

export function IntentBadge({ kind, onClick, title }: IntentBadgeProps) {
  if (kind === 'direct_answer') return null;
  const badge = <Badge variant={KIND_VARIANT[kind]}>{KIND_LABELS[kind]}</Badge>;
  if (!onClick) return <span title={title}>{badge}</span>;
  return (
    <span title={title}>
      <Button variant="ghost" size="sm" className="h-auto p-0 hover:opacity-80" onClick={onClick}>
        {badge}
      </Button>
    </span>
  );
}
